/* ============================================================
   WAKE SPAIN — Main JavaScript
   Vanilla JS: Observers, Animations, Interactions
   ============================================================ */

(function () {
  'use strict';

  /* ── Loading Screen ── */
  function initLoadingScreen() {
    const screen = document.getElementById('loading-screen');
    if (!screen) return;

    window.addEventListener('load', function () {
      setTimeout(function () {
        screen.classList.add('hidden');
        document.body.style.overflow = '';
      }, 1800);
    });

    // Fallback: remove after 3s no matter what
    setTimeout(function () {
      screen.classList.add('hidden');
      document.body.style.overflow = '';
    }, 3000);
  }

  /* ── Scroll Reveal (IntersectionObserver) ── */
  function initScrollReveal() {
    if (!('IntersectionObserver' in window)) {
      // Fallback: show all
      document.querySelectorAll('.reveal, .reveal--left, .reveal--right, .reveal--scale').forEach(function (el) {
        el.classList.add('revealed');
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.12,
      rootMargin: '0px 0px -40px 0px'
    });

    document.querySelectorAll('.reveal, .reveal--left, .reveal--right, .reveal--scale').forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ── Counter Animation ── */
  function animateCounter(el) {
    var target = parseInt(el.dataset.target, 10);
    var duration = parseInt(el.dataset.duration, 10) || 2000;
    var start = 0;
    var startTime = null;
    var suffix = el.dataset.suffix || '';
    var prefix = el.dataset.prefix || '';

    function easeOutQuart(t) {
      return 1 - Math.pow(1 - t, 4);
    }

    function step(timestamp) {
      if (!startTime) startTime = timestamp;
      var progress = Math.min((timestamp - startTime) / duration, 1);
      var value = Math.floor(easeOutQuart(progress) * target);
      el.textContent = prefix + value.toLocaleString() + suffix;

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        el.textContent = prefix + target.toLocaleString() + suffix;
        el.classList.add('counter-done');
      }
    }

    requestAnimationFrame(step);
  }

  function initCounters() {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('[data-counter]').forEach(function (el) {
        var target = el.dataset.target || 0;
        var suffix = el.dataset.suffix || '';
        var prefix = el.dataset.prefix || '';
        el.textContent = prefix + parseInt(target).toLocaleString() + suffix;
      });
      return;
    }

    var counterObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !entry.target.classList.contains('counter-done')) {
          animateCounter(entry.target);
          counterObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.5 });

    document.querySelectorAll('[data-counter]').forEach(function (el) {
      counterObserver.observe(el);
    });
  }

  /* ── Ripple Effect ── */
  function createRipple(e) {
    var btn = e.currentTarget;
    var existing = btn.querySelector('.ripple');
    if (existing) existing.remove();

    var circle = document.createElement('span');
    var diameter = Math.max(btn.clientWidth, btn.clientHeight);
    var radius = diameter / 2;
    var rect = btn.getBoundingClientRect();

    circle.style.width = circle.style.height = diameter + 'px';
    circle.style.left = (e.clientX - rect.left - radius) + 'px';
    circle.style.top = (e.clientY - rect.top - radius) + 'px';
    circle.classList.add('ripple');

    btn.appendChild(circle);

    circle.addEventListener('animationend', function () {
      circle.remove();
    });
  }

  function initRipples() {
    document.querySelectorAll('.btn, .btn-atc, .btn-buy-now').forEach(function (btn) {
      btn.addEventListener('click', createRipple);
    });
  }

  /* ── FAQ Accordion ── */
  function initFAQ() {
    document.querySelectorAll('.faq-trigger').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var item = this.closest('.faq-item');
        var isOpen = item.classList.contains('open');

        // Close all
        document.querySelectorAll('.faq-item.open').forEach(function (openItem) {
          openItem.classList.remove('open');
          openItem.querySelector('.faq-trigger').setAttribute('aria-expanded', 'false');
        });

        // Open clicked if it was closed
        if (!isOpen) {
          item.classList.add('open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ── Header scroll effect ── */
  function initHeader() {
    var header = document.querySelector('.site-header');
    if (!header) return;

    function onScroll() {
      if (window.scrollY > 20) {
        header.classList.add('scrolled');
      } else {
        header.classList.remove('scrolled');
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ── Mobile Menu ── */
  function initMobileMenu() {
    var hamburger = document.querySelector('.hamburger');
    var mobileMenu = document.querySelector('.mobile-menu');
    if (!hamburger || !mobileMenu) return;

    hamburger.addEventListener('click', function () {
      var isOpen = mobileMenu.classList.contains('open');
      mobileMenu.classList.toggle('open');
      hamburger.classList.toggle('active');
      hamburger.setAttribute('aria-expanded', String(!isOpen));
      document.body.style.overflow = isOpen ? '' : 'hidden';
    });

    // Close on link click
    mobileMenu.querySelectorAll('a').forEach(function (link) {
      link.addEventListener('click', function () {
        mobileMenu.classList.remove('open');
        hamburger.classList.remove('active');
        document.body.style.overflow = '';
      });
    });
  }

  /* ── Smooth Scroll for anchor links ── */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var href = this.getAttribute('href');
        if (href === '#') return;

        var target = document.querySelector(href);
        if (!target) return;

        e.preventDefault();
        var navHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--nav-height'), 10) || 72;
        var top = target.getBoundingClientRect().top + window.scrollY - navHeight - 16;

        window.scrollTo({ top: top, behavior: 'smooth' });
      });
    });
  }

  /* ── Particle System (Hero) ── */
  function initParticles() {
    var canvas = document.getElementById('particles-canvas');
    if (!canvas) return;

    // Skip particles if user prefers reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var ctx = canvas.getContext('2d');
    var particles = [];
    var animFrame;

    function resize() {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    }

    function Particle() {
      this.reset();
    }

    Particle.prototype.reset = function () {
      this.x = Math.random() * canvas.width;
      this.y = canvas.height + Math.random() * 100;
      this.size = Math.random() * 3 + 1;
      this.speedY = -(Math.random() * 0.8 + 0.3);
      this.speedX = (Math.random() - 0.5) * 0.5;
      this.opacity = Math.random() * 0.6 + 0.2;
      this.life = 0;
      this.maxLife = Math.random() * 200 + 100;
      this.color = Math.random() > 0.5 ? '#E50000' : '#FF4500';
    };

    Particle.prototype.update = function () {
      this.x += this.speedX;
      this.y += this.speedY;
      this.life++;

      var lifeRatio = this.life / this.maxLife;
      if (lifeRatio > 0.7) {
        this.opacity = (1 - lifeRatio) / 0.3 * this.opacity;
      }

      if (this.life >= this.maxLife || this.y < -20) {
        this.reset();
      }
    };

    Particle.prototype.draw = function () {
      ctx.save();
      ctx.globalAlpha = Math.max(0, this.opacity);
      ctx.fillStyle = this.color;
      ctx.shadowColor = this.color;
      ctx.shadowBlur = this.size * 2;
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    };

    // Init particles
    for (var i = 0; i < 40; i++) {
      var p = new Particle();
      p.life = Math.random() * p.maxLife; // stagger start
      particles.push(p);
    }

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(function (p) {
        p.update();
        p.draw();
      });
      animFrame = requestAnimationFrame(animate);
    }

    resize();
    animate();

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        resize();
      }, 200);
    });
  }

  /* ── Hero Slideshow ── */
  function initHeroSlideshow() {
    var slides = document.querySelectorAll('.hero-slide');
    var dots   = document.querySelectorAll('.hero-dot');
    if (slides.length < 2) return;

    var current = 0;
    var timer;

    function goTo(index) {
      var prev = current;
      current = (index + slides.length) % slides.length;
      if (prev === current) return;

      slides[prev].classList.add('exiting');
      slides[prev].classList.remove('active');
      setTimeout(function () { slides[prev].classList.remove('exiting'); }, 500);

      slides[current].classList.add('active');

      dots.forEach(function (d, i) { d.classList.toggle('active', i === current); });
    }

    function next() { goTo(current + 1); }

    function startTimer() { timer = setInterval(next, 3500); }
    function resetTimer() { clearInterval(timer); startTimer(); }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { goTo(i); resetTimer(); });
    });

    startTimer();
  }

  /* ── Hero Title Character Animation ── */
  function initHeroTitle() {
    var title = document.querySelector('.hero-title-animate');
    if (!title) return;

    // Already handled by CSS + class, just ensure chars get delay
    var chars = title.querySelectorAll('.char');
    chars.forEach(function (char, i) {
      char.style.animationDelay = (i * 55) + 'ms';
    });
  }

  /* ── Product Page: Gallery ── */
  function initProductGallery() {
    var mainImg = document.querySelector('.gallery-main-img');
    var thumbs = document.querySelectorAll('.gallery-thumb');
    var lightbox = document.querySelector('.lightbox-overlay');
    var lightboxImg = document.querySelector('.lightbox-img');

    if (!mainImg) return;

    // Thumbnail switching
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var src = this.dataset.src || this.querySelector('img').src;
        mainImg.src = src;
        mainImg.srcset = '';
        thumbs.forEach(function (t) { t.classList.remove('active'); });
        this.classList.add('active');
      });
    });

    // Lightbox
    if (lightbox) {
      document.querySelector('.gallery-main').addEventListener('click', function () {
        if (lightboxImg) lightboxImg.src = mainImg.src;
        lightbox.classList.add('open');
        document.body.style.overflow = 'hidden';
      });

      function closeLightbox() {
        lightbox.classList.remove('open');
        document.body.style.overflow = '';
      }

      lightbox.addEventListener('click', function (e) {
        if (e.target === lightbox) closeLightbox();
      });

      var closeBtn = lightbox.querySelector('.lightbox-close');
      if (closeBtn) closeBtn.addEventListener('click', closeLightbox);

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeLightbox();
      });
    }
  }

  /* ── Product Page: Quantity ── */
  function initQuantity() {
    var qtyInput = document.querySelector('.qty-input');
    var qtyMinus = document.querySelector('.qty-minus');
    var qtyPlus = document.querySelector('.qty-plus');

    if (!qtyInput) return;

    if (qtyMinus) {
      qtyMinus.addEventListener('click', function () {
        var val = parseInt(qtyInput.value, 10) || 1;
        if (val > 1) {
          qtyInput.value = val - 1;
          qtyInput.dispatchEvent(new Event('change'));
        }
      });
    }

    if (qtyPlus) {
      qtyPlus.addEventListener('click', function () {
        var val = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = val + 1;
        qtyInput.dispatchEvent(new Event('change'));
      });
    }

    qtyInput.addEventListener('change', function () {
      var val = parseInt(this.value, 10);
      if (isNaN(val) || val < 1) this.value = 1;
    });
  }

  /* ── Product Page: Add to Cart button animation ── */
  function initATCButton() {
    var btn = document.querySelector('.btn-atc');
    if (!btn) return;

    btn.addEventListener('click', function (e) {
      if (btn.classList.contains('success')) return;
      btn.classList.add('success');

      setTimeout(function () {
        btn.classList.remove('success');
      }, 2500);
    });
  }

  /* ── Product Page: Tabs ── */
  function initProductTabs() {
    var tabBtns = document.querySelectorAll('.tab-btn');
    var tabContents = document.querySelectorAll('.tab-content');

    if (!tabBtns.length) return;

    tabBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = this.dataset.tab;

        tabBtns.forEach(function (b) { b.classList.remove('active'); });
        tabContents.forEach(function (c) { c.classList.remove('active'); });

        this.classList.add('active');
        var content = document.querySelector('[data-tab-content="' + target + '"]');
        if (content) content.classList.add('active');
      });
    });
  }

  /* ── Parallax ── */
  function initParallax() {
    var parallaxEls = document.querySelectorAll('[data-parallax]');
    if (!parallaxEls.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    function onScroll() {
      var scrollY = window.scrollY;

      parallaxEls.forEach(function (el) {
        var speed = parseFloat(el.dataset.parallax) || 0.3;
        var rect = el.closest('.parallax-wrapper').getBoundingClientRect();
        var sectionScrollY = scrollY - (scrollY + rect.top);
        el.style.transform = 'translateY(' + (sectionScrollY * speed) + 'px)';
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ── Stagger children reveal ── */
  function initStaggerReveal() {
    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var children = entry.target.querySelectorAll('.reveal');
          children.forEach(function (child, i) {
            setTimeout(function () {
              child.classList.add('revealed');
            }, i * 100);
          });
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.stagger-children').forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ── Video placeholder click ── */
  function initVideoSection() {
    var placeholder = document.querySelector('.video-placeholder');
    if (!placeholder) return;

    placeholder.addEventListener('click', function () {
      var videoId = this.dataset.videoId;
      var videoType = this.dataset.videoType || 'youtube';
      if (!videoId) return;

      var iframe = document.createElement('iframe');
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.border = 'none';
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('allow', 'autoplay; fullscreen');

      if (videoType === 'youtube') {
        iframe.src = 'https://www.youtube.com/embed/' + videoId + '?autoplay=1&rel=0';
      } else if (videoType === 'tiktok') {
        iframe.src = 'https://www.tiktok.com/embed/' + videoId;
      }

      this.parentNode.replaceChild(iframe, this);
    });
  }

  /* ── Init everything on DOMContentLoaded ── */
  function init() {
    initLoadingScreen();
    initScrollReveal();
    initCounters();
    initRipples();
    initFAQ();
    initHeader();
    initMobileMenu();
    initSmoothScroll();
    initParticles();
    initHeroTitle();
    initHeroSlideshow();
    initStaggerReveal();
    initParallax();
    initVideoSection();

    // Product page specific
    if (document.querySelector('.product-layout')) {
      initProductGallery();
      initQuantity();
      initATCButton();
      initProductTabs();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/* ── Policy Modal ── */
(function () {
  var modal = document.getElementById('policy-modal');
  if (!modal) return;

  var titleEl = modal.querySelector('.policy-modal-title');
  var backdrop = modal.querySelector('.policy-modal-backdrop');
  var closeBtn = modal.querySelector('.policy-modal-close');

  var policyTitles = {
    privacy:  'Política de Privacidad',
    terms:    'Términos y Condiciones',
    refund:   'Política de Reembolso',
    shipping: 'Política de Envío'
  };

  function openPolicy(key) {
    modal.querySelectorAll('.policy-content').forEach(function (el) {
      el.hidden = el.dataset.policy !== key;
    });
    titleEl.textContent = policyTitles[key] || '';
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    closeBtn.focus();
  }

  function closeModal() {
    modal.hidden = true;
    document.body.style.overflow = '';
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-policy-modal]');
    if (trigger) {
      e.preventDefault();
      openPolicy(trigger.dataset.policyModal);
    }
  });

  backdrop.addEventListener('click', closeModal);
  closeBtn.addEventListener('click', closeModal);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !modal.hidden) closeModal();
  });
})();
